import logger from "../utils/logger";

function createFileValidationService() {

    function extractBankAccount(csvRows, bankAccRow) {
        if (!csvRows || csvRows.length === 0) {
            return null;
        }

        // Define the search range based on bankAccRow parameter
        let searchRows = [];
        
        if (bankAccRow !== null && bankAccRow !== undefined && bankAccRow > 0) {
            // Use specific row (convert 1-based to 0-based)
            const targetRowIndex = bankAccRow - 1;
            if (targetRowIndex < csvRows.length) {
                searchRows = [targetRowIndex];
            }
        } else {
            // Fallback: search through first 10 rows if no specific row specified
            searchRows = Array.from({ length: Math.min(10, csvRows.length) }, (_, i) => i);
        }

        // Search for bank account pattern in the specified rows
        for (const rowIndex of searchRows) {
            const rowData = csvRows[rowIndex];
            if (rowData && rowData.length > 0) {
                // Check each cell in this row for bank account pattern
                for (let cellIndex = 0; cellIndex < rowData.length; cellIndex++) {
                    const cell = rowData[cellIndex];
                    if (cell && cell.trim().length > 0) {
                        const trimmedCell = cell.trim().replace(/^["']|["']$/g, '');
                        // Look for bank account pattern: XX-XXXX-XXXXXXX-XXX (dashes optional)
                        const bankAccountMatch = trimmedCell.match(/\d{2}-?\d{4}-?\d{7,}-?\d{3}/);
                        if (bankAccountMatch) {
                            return bankAccountMatch[0];
                        }
                    }
                }
            }
        }

        return null;
    }

    function extractFieldHeaders(csvRows, headerRow) {
        if (!csvRows || csvRows.length === 0) {
            return [];
        }

        let targetRowIndex = -1;

        if (headerRow !== null && headerRow !== undefined) {
            // Convert 1-based headerRow to 0-based array index
            targetRowIndex = headerRow - 1;
        } else {
            // Legacy fallback: try to auto-detect header row for unspecified cases
            for (let i = 0; i < Math.min(10, csvRows.length); i++) {
                const row = csvRows[i];
                if (row && row.length > 5) {
                    // Look for header-like patterns (contains "Date", "Amount", etc.)
                    const rowText = row.join("|").toLowerCase();
                    if (rowText.includes("date") && rowText.includes("amount") &&
                        (rowText.includes("ccy") || rowText.includes("reference"))) {
                        targetRowIndex = i;
                        break;
                    }
                }
            }
        }

        if (csvRows.length > targetRowIndex && targetRowIndex >= 0) {
            return csvRows[targetRowIndex]
                .map(header => (header || "").trim())
                .filter(header => header.length > 0);
        }

        return [];
    }

    function isFieldFound(requiredField, actualFields) {
        const requiredLower = requiredField.toLowerCase().trim();
        
        return actualFields.some(actualField => {
            const actualLower = actualField.toLowerCase().trim();
            
            // Exact match (case-insensitive)
            if (actualLower === requiredLower) {
                return true;
            }
            
            // Prevent short field matches that cause false positives
            if (requiredLower.length < 4 || actualLower.length < 4) {
                return false;
            }
            
            // Allow partial matches only if one completely contains the other
            // and both fields are reasonably long to avoid false positives
            return (actualLower.includes(requiredLower) && requiredLower.length >= 4) ||
                   (requiredLower.includes(actualLower) && actualLower.length >= 4);
        });
    }

    function areAllFieldsFound(requiredFields, actualFields) {
        return requiredFields.length > 0 &&
               requiredFields.every(field => isFieldFound(field, actualFields));
    }

    function validateBankAccount(foundAccount, expectedAccount, bankAccRow) {
        // If no expected account is defined (null, undefined, or empty), skip bank account validation
        if (!expectedAccount || expectedAccount === null || expectedAccount.trim() === '') {
            return null; // Indicate that bank account validation should be skipped
        }
        
        // If BankAccRow is 0, skip bank account validation
        if (bankAccRow === 0) {
            return null; // Indicate that bank account validation should be skipped
        }

        if (!foundAccount) {
            return false; // Account not found but was expected
        }

        // Normalize both accounts by removing dashes for comparison
        const normalizedFound = foundAccount.replace(/-/g, '');
        const normalizedExpected = expectedAccount.replace(/-/g, '');
        return normalizedFound === normalizedExpected;
    }

    function validateFile({ csvRows, fileType, requiredFields }) {
        try {
            // Extract bank account
            const foundBankAccount = extractBankAccount(csvRows, fileType.bankAccRow);

            // Extract field headers
            const actualFields = extractFieldHeaders(csvRows, fileType.headerRow);

            // Validate bank account
            const bankAccountValidation = validateBankAccount(foundBankAccount, fileType.expectedBankAccNo, fileType.bankAccRow);
            const bankAccountValid = bankAccountValidation !== false; // true if valid or null (skipped)

            // Validate fields
            const allFieldsFound = areAllFieldsFound(requiredFields, actualFields);

            // Check individual field status
            const fieldStatus = requiredFields.map(field => ({
                name: field,
                found: isFieldFound(field, actualFields)
            }));

            return {
                isValid: allFieldsFound && bankAccountValid,
                bankAccount: {
                    found: foundBankAccount,
                    expected: fileType.expectedBankAccNo,
                    isValid: bankAccountValidation === null ? null : bankAccountValidation, // null means skipped
                    skipped: bankAccountValidation === null
                },
                fields: {
                    actual: actualFields,
                    required: requiredFields,
                    allFound: allFieldsFound,
                    status: fieldStatus
                }
            };
        } catch (error) {
            logger.error("Error during file validation:", error);
            return {
                isValid: false,
                error: error.message,
                bankAccount: { found: null, expected: null, isValid: false, skipped: false },
                fields: { actual: [], required: [], allFound: false, status: [] }
            };
        }
    }

    return {
        extractBankAccount,
        extractFieldHeaders,
        isFieldFound,
        areAllFieldsFound,
        validateBankAccount,
        validateFile
    };
}

// Export the factory function
export { createFileValidationService };

// Export a singleton instance as default for backward compatibility
const fileValidationService = createFileValidationService();
export default fileValidationService;